import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, RotateCcw, ArrowUpRight, Sun, Moon, MapPin, CalendarDays } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { computeChoghadiya, formatTime } from "@/lib/choghadiya";
import LocationPicker from "./LocationPicker";
import BrandedCalendar from "./BrandedCalendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * AuspiciousToday — quick-access table of the day's auspicious Chogadiya
 * windows (Amrit · Shubh · Labh · Char). Sits directly below the hero so
 * a returning user can scan the day at a glance.
 *
 * Date control: chevron prev / chevron next / "today" reset.
 * Only this table re-computes; the rest of the page stays anchored to "now".
 */
const STATE_HEX = {
  amrit_best:    "#9FCBC6", // softer cool teal for higher legibility on dark
  shubh_good:    "#E8B3C0", // softer rose
  labh_gain:     "#E5C982", // brass-light
  char_neutral:  "#D8D6CC", // moonstone
};

const STATE_LABEL = {
  amrit_best:    "Best",
  shubh_good:    "Good",
  labh_gain:     "Gain",
  char_neutral:  "Neutral",
};

const AUSPICIOUS_KEYS = ["amrit", "shubh", "labh", "char"];

function isSameDay(a, b, tz) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(a) === fmt.format(b);
}

function durationMin(s, e) {
  return Math.round((e.getTime() - s.getTime()) / 60000);
}

export default function AuspiciousToday({ onSlotClick }) {
  const { location } = useLocation();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [calOpen, setCalOpen] = useState(false);
  const [now, setNow] = useState(new Date());

  // Tick "now" every second so the "live" badge stays current.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const cho = useMemo(
    () => computeChoghadiya(location.lat, location.lng, selectedDate, location.timezone),
    [location.lat, location.lng, location.timezone, selectedDate]
  );

  const auspiciousSlots = useMemo(() => {
    return [...cho.day, ...cho.night]
      .filter((s) => AUSPICIOUS_KEYS.includes(s.key))
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [cho]);

  const tz = location.timezone;
  const isToday = isSameDay(selectedDate, now, tz);

  const changeDay = (deltaDays) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + deltaDays);
    setSelectedDate(d);
  };
  const resetToday = () => setSelectedDate(new Date());

  const dayLabel = selectedDate.toLocaleDateString("en-US", {
    weekday: "long", month: "short", day: "numeric", year: "numeric", timeZone: tz,
  });

  return (
    <section
      id="auspicious-today"
      className="relative max-w-[1440px] mx-auto px-5 sm:px-6 md:px-10 pt-8 sm:pt-12 md:pt-16"
      data-testid="auspicious-today-section"
    >
      {/* Section header */}
      <div className="flex items-end justify-between mb-3">
        <span className="section-num">— quick scan</span>
        <span className="section-num hidden md:inline">auspicious windows</span>
      </div>
      <div className="divider-sweep mb-5 sm:mb-8" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-10 items-start">
        {/* Left: title + date picker */}
        <div className="lg:col-span-4">
          <h2 className="display text-[clamp(1.8rem,4.5vw,3rem)] leading-[0.95] mb-3">
            Today&apos;s <em style={{ color: "var(--accent-soft)" }}>auspicious</em> windows.
          </h2>
          <p
            className="font-display text-sm leading-relaxed max-w-md mb-4"
            style={{ color: "var(--ink-2)" }}
          >
            The day&apos;s good times at a glance. Amrit, Shubh, Labh, Char. Tap any row to see what it&apos;s best for. Move forward or back to plan ahead.
          </p>

          {/* Location picker (compact) — clearer call to action so users
              know they can switch cities right here. */}
          <div className="mb-4" data-testid="auspicious-location-block">
            <div className="eyebrow mb-2 flex items-center gap-1.5">
              <MapPin size={11} />
              your sky over · tap to change
            </div>
            <LocationPicker compact />
          </div>

          {/* Date control — single sleek pill */}
          <div
            className="inline-flex items-center gap-1 rounded-2xl p-1"
            style={{
              background: "rgba(14,31,30,0.55)",
              border: "1px solid rgba(216,185,122,0.28)",
              backdropFilter: "blur(12px)",
            }}
            data-testid="auspicious-date-control"
          >
            <button
              onClick={() => changeDay(-1)}
              data-testid="auspicious-prev-day"
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-[rgba(216,185,122,0.12)]"
              style={{ color: "var(--ink)" }}
              aria-label="Previous day"
            >
              <ChevronLeft size={14} />
            </button>

            <div className="px-2 sm:px-3 min-w-[150px] sm:min-w-[180px] text-center">
              <div
                className="font-mono uppercase tracking-[0.16em] text-[9px] sm:text-[10px]"
                style={{ color: "var(--accent-soft)" }}
              >
                {isToday ? "today" : "selected day"}
              </div>
              <div
                className="font-display"
                style={{
                  color: "var(--ink)",
                  fontSize: "clamp(0.82rem, 1.2vw, 0.95rem)",
                  letterSpacing: "0.01em",
                  lineHeight: 1.15,
                }}
                data-testid="auspicious-date-label"
              >
                {dayLabel}
              </div>
            </div>

            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="auspicious-calendar-trigger"
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-[rgba(216,185,122,0.12)]"
                  style={{ color: "var(--accent-soft)" }}
                  aria-label="Pick a date from the calendar"
                  type="button"
                >
                  <CalendarDays size={14} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={10}
                className="w-auto p-2 border-0"
                style={{
                  background: "rgba(14,31,30,0.97)",
                  border: "1px solid rgba(216,185,122,0.30)",
                  borderRadius: 16,
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 24px 60px -16px rgba(0,0,0,0.6)",
                }}
              >
                <BrandedCalendar
                  selected={selectedDate}
                  onSelect={(d) => {
                    if (d) {
                      // Normalize to noon-local (see Timeline.jsx for rationale).
                      const noon = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
                      setSelectedDate(noon);
                      setCalOpen(false);
                    }
                  }}
                  data-testid="auspicious-calendar"
                />
              </PopoverContent>
            </Popover>

            <button
              onClick={() => changeDay(1)}
              data-testid="auspicious-next-day"
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-[rgba(216,185,122,0.12)]"
              style={{ color: "var(--ink)" }}
              aria-label="Next day"
            >
              <ChevronRight size={14} />
            </button>

            {!isToday && (
              <button
                onClick={resetToday}
                data-testid="auspicious-today-reset"
                className="ml-0.5 px-2.5 h-8 sm:h-9 rounded-xl flex items-center gap-1 transition-colors hover:bg-[rgba(216,185,122,0.12)]"
                style={{ color: "var(--accent-soft)" }}
                aria-label="Back to today"
              >
                <RotateCcw size={11} />
                <span className="font-mono uppercase tracking-[0.14em] text-[9px]">today</span>
              </button>
            )}
          </div>

          <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ink-faint)" }}>
            sunrise {formatTime(cho.sunrise, tz)} · sunset {formatTime(cho.sunset, tz)} · {location.displayName}
          </div>
        </div>

        {/* Right: the table */}
        <div className="lg:col-span-8">
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: "rgba(14,31,30,0.55)",
              border: "1px solid rgba(216,185,122,0.22)",
              backdropFilter: "blur(14px)",
            }}
          >
            {/* Header row */}
            <div
              className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 font-mono uppercase tracking-[0.18em] text-[10px]"
              style={{
                color: "var(--accent-soft)",
                borderBottom: "1px solid rgba(216,185,122,0.18)",
              }}
            >
              <div className="col-span-3">window</div>
              <div className="col-span-2">intent</div>
              <div className="col-span-2">half</div>
              <div className="col-span-3">time</div>
              <div className="col-span-1 text-right">dur</div>
              <div className="col-span-1 text-right">·</div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={selectedDate.toDateString() + location.name}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.45, ease: [0.7, 0, 0.2, 1] }}
                data-testid="auspicious-table-body"
              >
                {auspiciousSlots.length === 0 && (
                  <div className="px-5 py-8 text-center font-display italic" style={{ color: "var(--ink-muted)" }}>
                    No auspicious windows could be computed for this day.
                  </div>
                )}

                {auspiciousSlots.map((slot, idx) => {
                  const color = STATE_HEX[slot.stateKey];
                  const isNow = isToday && now >= slot.start && now < slot.end;
                  const isPast = isToday && now >= slot.end;
                  const isUpcoming = !isNow && !isPast;
                  const dur = durationMin(slot.start, slot.end);
                  const status = isNow ? "now" : isPast ? "past" : "upcoming";

                  return (
                    <motion.button
                      key={`${slot.kind}-${slot.index}-${slot.key}`}
                      onClick={() => onSlotClick?.(slot)}
                      data-testid={`auspicious-row-${idx}`}
                      whileHover={{ x: 2 }}
                      transition={{ duration: 0.3 }}
                      className="w-full text-left grid grid-cols-12 gap-2.5 sm:gap-3 px-3.5 sm:px-5 py-3 sm:py-4 transition-colors hover:bg-[rgba(216,185,122,0.06)]"
                      style={{
                        borderBottom: idx === auspiciousSlots.length - 1 ? "none" : "1px solid rgba(216,185,122,0.10)",
                        opacity: isPast ? 0.45 : 1,
                      }}
                    >
                      {/* Window — name + state pill */}
                      <div className="col-span-12 md:col-span-3 flex items-center gap-3">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{
                            background: color,
                            boxShadow: isNow ? `0 0 12px ${color}cc` : "none",
                          }}
                        />
                        <div className="min-w-0">
                          <div
                            className="font-display"
                            style={{
                              color: "var(--ink)",
                              fontSize: "clamp(1.05rem, 1.7vw, 1.4rem)",
                              lineHeight: 1.05,
                              fontWeight: 500,
                            }}
                          >
                            {slot.name}
                          </div>
                          <div
                            className="md:hidden font-mono uppercase tracking-[0.18em] text-[10px] mt-0.5"
                            style={{ color }}
                          >
                            {STATE_LABEL[slot.stateKey]} · {slot.kind}
                          </div>
                        </div>
                      </div>

                      {/* Intent label (desktop only) */}
                      <div className="hidden md:flex md:col-span-2 items-center">
                        <span
                          className="font-display italic"
                          style={{
                            color,
                            fontSize: "0.95rem",
                            letterSpacing: "0.01em",
                          }}
                        >
                          {STATE_LABEL[slot.stateKey]}
                        </span>
                      </div>

                      {/* Half */}
                      <div className="hidden md:flex md:col-span-2 items-center gap-2" style={{ color: "var(--ink-muted)" }}>
                        {slot.kind === "day" ? <Sun size={13} /> : <Moon size={13} />}
                        <span className="font-mono uppercase tracking-[0.16em] text-[10px]">
                          {slot.kind}
                        </span>
                      </div>

                      {/* Time */}
                      <div className="col-span-7 md:col-span-3 flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <span
                          className="numeric"
                          style={{
                            color,
                            fontSize: "clamp(0.85rem, 1.3vw, 1.05rem)",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {formatTime(slot.start, tz)}
                        </span>
                        <span className="font-mono text-[10px]" style={{ color: "var(--ink-faint)" }}>→</span>
                        <span
                          className="numeric"
                          style={{
                            color: "var(--ink)",
                            fontSize: "clamp(0.8rem, 1.2vw, 1rem)",
                            letterSpacing: "0.02em",
                            opacity: 0.85,
                          }}
                        >
                          {formatTime(slot.end, tz)}
                        </span>
                      </div>

                      {/* Duration */}
                      <div className="col-span-3 md:col-span-1 flex items-center md:justify-end">
                        <span
                          className="font-mono text-xs"
                          style={{ color: "var(--ink-muted)", letterSpacing: "0.04em" }}
                        >
                          {dur >= 60 ? `${Math.floor(dur / 60)}h ${dur % 60}m` : `${dur}m`}
                        </span>
                      </div>

                      {/* Status + arrow */}
                      <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-2">
                        {isNow ? (
                          <span
                            className="px-2 py-0.5 rounded-full font-mono uppercase tracking-[0.18em] text-[9px]"
                            style={{
                              background: color,
                              color: "#0F1F1E",
                              fontWeight: 700,
                            }}
                          >
                            now
                          </span>
                        ) : (
                          <span
                            className="font-mono uppercase tracking-[0.16em] text-[9px] hidden sm:inline"
                            style={{ color: "var(--ink-faint)" }}
                          >
                            {status}
                          </span>
                        )}
                        <ArrowUpRight size={14} style={{ color: "var(--accent-soft)", opacity: 0.6 }} />
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
